import * as React from "react";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { AntDTable } from "@/common/components/ui";
import { Card, CardContent } from "@/common/components/ui/card";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  CalendarDays,
  RefreshCw,
  Download,
} from "lucide-react";
import { TableColumnsType } from "antd";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import HolidayService from "@/services/FinEaseHomeAndAdmin/APIs/HolidayService";
import {
  HolidayListResponse,
  HolidayRecord,
} from "@/utils/types/Holiday/Holiday";
import { notify } from "@/common/components/common/Notification";
import DeleteConfirmationModal from "@/projects/FinEaseHomeAndAdmin/components/DeleteConfirmationModal";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";

const EXPORT_PAGE_SIZE = 100;
const MAX_EXPORT_PAGES = 500;

function holidayDate(h: HolidayRecord): string {
  return h.date ?? h.holiday_date ?? "";
}

interface HolidayRow {
  id: string;
  srNo: number;
  date: string;
  day: string;
  description: string;
  created_on?: string;
  updated_on?: string;
}

function mapResultsToRows(
  results: HolidayRecord[],
  page: number,
  pageSize: number
): HolidayRow[] {
  return results.map((h, i) => ({
    id: h.id,
    srNo: h.sr_no ?? (page - 1) * pageSize + i + 1,
    date: holidayDate(h),
    day: h.day ?? "",
    description: h.description ?? "",
    created_on: h.created_on,
    updated_on: h.updated_on,
  }));
}

function matchesHolidaySearch(row: HolidayRow, q: string): boolean {
  if (!q) {
    return true;
  }
  const d = row.description.toLowerCase();
  const day = row.day.toLowerCase();
  const date = row.date.toLowerCase();
  return d.includes(q) || day.includes(q) || date.includes(q);
}

async function fetchAllHolidaysForExport(
  search?: string
): Promise<HolidayRow[]> {
  const acc: HolidayRecord[] = [];
  for (let p = 1; p <= MAX_EXPORT_PAGES; p += 1) {
    const res = await HolidayService.getHolidays(p, EXPORT_PAGE_SIZE, search);
    const batch = res.results ?? [];
    acc.push(...batch);
    if (batch.length < EXPORT_PAGE_SIZE) {
      break;
    }
  }
  const flat = acc.map((h, i) => ({
    id: h.id,
    srNo: h.sr_no ?? i + 1,
    date: holidayDate(h),
    day: h.day ?? "",
    description: h.description ?? "",
    created_on: h.created_on,
    updated_on: h.updated_on,
  }));
  const q = (search ?? "").trim().toLowerCase();
  if (!q) {
    return flat;
  }
  return flat.filter(row => matchesHolidaySearch(row, q));
}

const HolidaysPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      300
    );
    return () => window.clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState<{
    id: string;
    description: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const {
    data: holidaysResponse,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<HolidayListResponse>({
    queryKey: ["holidays", page, pageSize, debouncedSearch],
    queryFn: () =>
      HolidayService.getHolidays(page, pageSize, debouncedSearch || undefined),
    enabled: !!page && !!pageSize,
  });

  const total = holidaysResponse?.count ?? 0;
  const tableRows = useMemo(
    () => mapResultsToRows(holidaysResponse?.results ?? [], page, pageSize),
    [holidaysResponse?.results, page, pageSize]
  );

  const filteredHolidays = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    if (!q) {
      return tableRows;
    }
    return tableRows.filter(row => matchesHolidaySearch(row, q));
  }, [tableRows, debouncedSearch]);

  const handleDeleteClick = useCallback((id: string, description: string) => {
    setHolidayToDelete({ id, description });
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!holidayToDelete) {
      return;
    }

    try {
      setActionLoading(true);
      const res: any = await HolidayService.deleteHoliday(holidayToDelete.id);
      if (res?.success !== false) {
        notify.success(
          res?.message ??
            `${holidayToDelete.description} has been deleted successfully.`
        );
      } else {
        notify.error(res?.message ?? "Could not delete holiday.");
      }
      setDeleteModalOpen(false);
      setHolidayToDelete(null);
      refetch();
    } catch (error: any) {
      console.error("Error deleting holiday:", error);
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to delete holiday"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setHolidayToDelete(null);
  };

  const handleSyncData = async () => {
    try {
      const result = await refetch();
      if (result.isError) {
        notify.error(
          "Sync failed",
          (result.error as Error)?.message ?? "Could not refresh holidays."
        );
        return;
      }
      notify.success("Synced", "Holiday list updated from server.");
    } catch (e: unknown) {
      notify.error(
        "Sync failed",
        e instanceof Error ? e.message : "Could not refresh holidays."
      );
    }
  };

  const handleExportXlsx = async () => {
    setExportLoading(true);
    try {
      let exportList: HolidayRow[];

      if (selectedRowKeys.length > 0) {
        // Export only selected rows (already in memory)
        const selectedSet = new Set(selectedRowKeys.map(String));
        exportList = filteredHolidays.filter(h =>
          selectedSet.has(String(h.id))
        );
        if (exportList.length === 0) {
          notify.error("No data", "Selected rows not found in current view.");
          return;
        }
      } else {
        // Export all (existing behaviour)
        exportList = await fetchAllHolidaysForExport(
          debouncedSearch || undefined
        );
        if (exportList.length === 0) {
          notify.error("No data", "No holidays to export.");
          return;
        }
      }

      const rows = exportList.map(h => ({
        "SR No": h.srNo,
        Date: h.date,
        Day: h.day,
        Description: h.description,
        "Created On": h.created_on ?? "",
        "Updated On": h.updated_on ?? "",
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Holidays");
      const datePart = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `holidays_${datePart}.xlsx`);
      notify.success(
        "Exported",
        `${rows.length} holiday(s) downloaded as Excel.`
      );
    } catch (e: unknown) {
      notify.error(
        "Export failed",
        e instanceof Error ? e.message : "Could not export holidays."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const columns: TableColumnsType<HolidayRow> = useMemo(
    () => [
      {
        title: "SR NO",
        dataIndex: "srNo",
        key: "srNo",
        width: 100,
        render: text => <p className="font-medium text-slate-700">{text}</p>,
      },
      {
        title: "DATE",
        dataIndex: "date",
        key: "date",
        width: 150,
        render: text => <p className="font-medium text-slate-700">{text}</p>,
      },
      {
        title: "DAY",
        dataIndex: "day",
        key: "day",
        width: 150,
      },
      {
        title: "DESCRIPTION",
        dataIndex: "description",
        key: "description",
        width: 300,
      },
      {
        title: "ACTIONS",
        key: "actions",
        fixed: "right" as const,
        width: 120,
        render: (_, holiday) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                navigate(
                  finEaseHomeNavigationRoutes.EDIT_CMS_HOLIDAY(holiday.id)
                )
              }
              className="h-8 w-8 rounded-lg text-slate-500"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDeleteClick(holiday.id, holiday.description)}
              className="h-8 w-8 rounded-lg text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [navigate, handleDeleteClick]
  );

  return (
    <div className="space-y-6 p-6 font-sans">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <CalendarDays className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Holidays
              </h1>
              <p className="text-white/70">Manage your holiday list here.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => void handleSyncData()}
              disabled={isFetching}
            >
              <RefreshCw className="h-4 w-4" /> Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => void handleExportXlsx()}
              disabled={exportLoading || selectedRowKeys.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRowKeys.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRowKeys.length}
                </span>
              )}
            </Button>
            <Button
              onClick={() =>
                navigate(finEaseHomeNavigationRoutes.CREATE_CMS_HOLIDAY)
              }
              className="gap-2 bg-white text-secondary hover:bg-white/90"
            >
              <Plus className="h-4 w-4" /> Add Holiday
            </Button>
          </div>
        </div>
      </div>

      <Card className="rounded-xl border-none shadow-sm">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by description, day, or date..."
                className="h-11 rounded-xl bg-gray-50 pl-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="overflow-hidden rounded-md border">
        <AntDTable
          data={filteredHolidays}
          columns={columns}
          rowKey="id"
          loading={isLoading || isFetching}
          showSettings
          rowSelection={{
            type: "checkbox",
            selectedRowKeys,
            onChange: keys => setSelectedRowKeys(keys),
            preserveSelectedRowKeys: false,
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
          emptyText="No holidays found"
        />
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Holiday"
        message="Are you sure you want to delete"
        itemName={holidayToDelete?.description ?? ""}
        isLoading={actionLoading}
        confirmText="Yes, Delete"
        cancelText="No"
      />
    </div>
  );
};

export default HolidaysPage;
